/**
 * EditGroupDialog Component
 *
 * Dialog for editing an existing Schema Group.
 * Allows updating name, description, and database connection.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { groupApi, SchemaGroup, UpdateGroupInput } from '@/services/api/groupApi';
import { serverApi, DatabaseConnection } from '@/services/api/serverApi';
import * as errorUtils from '@/utils/errors';

interface EditGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: SchemaGroup | null;
  onSuccess: () => void;
}

export const EditGroupDialog: React.FC<EditGroupDialogProps> = ({
  open,
  onOpenChange,
  group,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<string>('');
  const [databaseConnections, setDatabaseConnections] = useState<DatabaseConnection[]>([]);
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when group changes
  useEffect(() => {
    if (group) {
      setName(group.name || '');
      setDescription(group.description || '');
      setSelectedDatabaseId(group.databaseConnectionId || '');
    }
  }, [group]);

  // Fetch database connections when dialog opens
  useEffect(() => {
    if (open) {
      fetchDatabases();
    }
  }, [open]);

  const fetchDatabases = async () => {
    setIsLoadingDatabases(true);
    try {
      const connections = await serverApi.listConnections();
      setDatabaseConnections(connections);
    } catch {
      // Non-critical - continue with empty list
      setDatabaseConnections([]);
    } finally {
      setIsLoadingDatabases(false);
    }
  };

  // Reset form when dialog closes
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setError(null);
    }
    onOpenChange(isOpen);
  }, [onOpenChange]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!group) return;

    if (!name.trim()) {
      setError('Group name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const input: UpdateGroupInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        databaseConnectionId: selectedDatabaseId || null,
      };

      await groupApi.updateGroup(group.id, input);
      handleOpenChange(false);
      onSuccess();
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to update group');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [name, description, selectedDatabaseId, group, handleOpenChange, onSuccess]);

  // Check if database connection changed
  const databaseChanged = group && (selectedDatabaseId || null) !== (group.databaseConnectionId || null);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            Edit Schema Group
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="edit-group-name">Name *</Label>
            <Input
              id="edit-group-name"
              placeholder="Group name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="edit-group-description">Description</Label>
            <Input
              id="edit-group-description"
              placeholder="Optional description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Database Connection */}
          <div className="space-y-2">
            <Label htmlFor="edit-group-database">Database Connection</Label>
            <Select
              value={selectedDatabaseId || '__default__'}
              onValueChange={(v) => setSelectedDatabaseId(v === '__default__' ? '' : v)}
              disabled={isLoadingDatabases}
            >
              <SelectTrigger id="edit-group-database">
                <SelectValue placeholder={isLoadingDatabases ? "Loading..." : "Default Connection"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Default Connection</SelectItem>
                {databaseConnections.map((db) => (
                  <SelectItem key={db.id} value={db.id}>
                    {db.name}{db.isDefault ? ' (System Default)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              All schemas in this group use this database
            </p>
          </div>

          {/* Database Change Warning */}
          {databaseChanged && (
            <div className="p-3 rounded-lg bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400 text-sm">
              <strong>Warning:</strong> Changing the database connection will affect where new data
              is stored. Existing data will remain in the previous database and may need to be
              migrated manually.
            </div>
          )}

          {/* Slug (read-only) */}
          {group?.slug && (
            <div className="space-y-2">
              <Label>Slug</Label>
              <div className="px-3 py-2 bg-muted/50 rounded-md text-sm text-muted-foreground">
                {group.slug}
              </div>
              <p className="text-xs text-muted-foreground">
                Slug cannot be changed (used in collection names)
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditGroupDialog;
